import React, { useState, useEffect } from 'react';
import useDiagnostics from '../hooks/useDiagnostics';
import { useWeb3 } from '../contexts/Web3Context';
import './DiagnosticsView.css';

import { ethers } from 'ethers'; // Added import

const DiagnosticsView = () => {
    const { deviceInfo, walletStatus, networkStatus, logs } = useDiagnostics();
    const { connectWallet, forceRefresh, disconnectWallet, wallet, contract, addLog } = useWeb3(); // Destructure needed items
    const [autoScroll, setAutoScroll] = useState(true);

    const handleTestBalance = async () => {
        if (!contract || !wallet) {
            alert("Connect wallet first!");
            return;
        }
        try {
            const addr = wallet.accounts[0].address;
            addLog(`[ManualTest] Fetching balance for ${addr}...`);
            const bal = await contract.balanceOf(addr);
            addLog(`[ManualTest] Raw Balance Result: ${bal.toString()}`);
            alert(`Balance: ${ethers.formatUnits(bal, 18)} XCO`);
        } catch (e) {
            addLog(`[ManualTest] Error: ${e.message}`);
            alert("Error fetching balance: " + e.message);
        }
    };

    // Auto-scroll logs
    useEffect(() => {
        if (autoScroll) {
            const logsContainer = document.getElementById('logs-container');
            if (logsContainer) {
                logsContainer.scrollTop = logsContainer.scrollHeight;
            }
        }
    }, [logs, autoScroll]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copiado al portapapeles');
    };

    const exportLogs = () => {
        const data = {
            timestamp: new Date().toISOString(),
            deviceInfo,
            walletStatus,
            networkStatus,
            logs,
        };
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `xcoin-diagnostics-${Date.now()}.json`;
        a.click();
    };

    const StatusIndicator = ({ status }) => (
        <span className={`status-indicator status-${status ? 'success' : 'error'}`}>
            {status ? '✅' : '❌'}
        </span>
    );

    return (
        <div className="diagnostics-view">
            <div className="diagnostics-header">
                <h1>🔍 XCOIN Diagnostics</h1>
                <p className="subtitle">Información técnica para debugging</p>
            </div>

            {/* Device Info */}
            <section className="diagnostics-section">
                <h2>📱 Device Information</h2>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Device Type:</label>
                        <span className="info-value">
                            {deviceInfo.isMobile ? '📱 Mobile' : deviceInfo.isTablet ? '📱 Tablet' : '💻 Desktop'}
                        </span>
                    </div>
                    <div className="info-item">
                        <label>Operating System:</label>
                        <span className="info-value">{deviceInfo.os}</span>
                    </div>
                    <div className="info-item">
                        <label>Browser:</label>
                        <span className="info-value">{deviceInfo.browser} {deviceInfo.browserVersion}</span>
                    </div>
                    <div className="info-item">
                        <label>Platform:</label>
                        <span className="info-value">{deviceInfo.platform}</span>
                    </div>
                    <div className="info-item">
                        <label>Language:</label>
                        <span className="info-value">{deviceInfo.language}</span>
                    </div>
                    <div className="info-item">
                        <label>Screen Resolution:</label>
                        <span className="info-value">{deviceInfo.screenWidth} x {deviceInfo.screenHeight}</span>
                    </div>
                    <div className="info-item">
                        <label>Viewport Size:</label>
                        <span className="info-value">{deviceInfo.viewportWidth} x {deviceInfo.viewportHeight}</span>
                    </div>
                    <div className="info-item full-width">
                        <label>User Agent:</label>
                        <div className="user-agent-box">
                            <code>{deviceInfo.userAgent}</code>
                            <button
                                onClick={() => copyToClipboard(deviceInfo.userAgent)}
                                className="copy-btn"
                            >
                                📋 Copy
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* WalletConnect Status */}
            <section className="diagnostics-section">
                <h2>🔌 WalletConnect Status</h2>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Connected:</label>
                        <span className="info-value">
                            <StatusIndicator status={walletStatus.isConnected} />
                            {walletStatus.isConnected ? 'Yes' : 'No'}
                        </span>
                    </div>
                    <div className="info-item">
                        <label>Wallet Address:</label>
                        <span className="info-value">
                            {walletStatus.address ? (
                                <>
                                    <code>{walletStatus.address.slice(0, 6)}...{walletStatus.address.slice(-4)}</code>
                                    <button
                                        onClick={() => copyToClipboard(walletStatus.address)}
                                        className="copy-btn-small"
                                    >
                                        📋
                                    </button>
                                </>
                            ) : (
                                'Not connected'
                            )}
                        </span>
                    </div>
                    <div className="info-item">
                        <label>Has Wallet Object:</label>
                        <span className="info-value">
                            <StatusIndicator status={walletStatus.hasWallet} />
                            {walletStatus.hasWallet ? 'Yes' : 'No'}
                        </span>
                    </div>
                </div>
            </section>

            {/* Network Status */}
            <section className="diagnostics-section">
                <h2>🌐 Network Status</h2>
                <div className="info-grid">
                    <div className="info-item">
                        <label>Internet Connection:</label>
                        <span className="info-value">
                            <StatusIndicator status={networkStatus.online} />
                            {networkStatus.online ? 'Online' : 'Offline'}
                        </span>
                    </div>
                    <div className="info-item">
                        <label>Current Chain:</label>
                        <span className="info-value">{networkStatus.chainName}</span>
                    </div>
                    <div className="info-item">
                        <label>Chain ID:</label>
                        <span className="info-value">
                            {networkStatus.chainId || 'N/A'}
                        </span>
                    </div>
                    <div className="info-item">
                        <label>Expected Chain ID:</label>
                        <span className="info-value">{networkStatus.expectedChainId}</span>
                    </div>
                    <div className="info-item">
                        <label>Correct Chain:</label>
                        <span className="info-value">
                            <StatusIndicator status={networkStatus.isCorrectChain} />
                            {networkStatus.isCorrectChain ? 'Yes (BSC Mainnet)' : 'No - Switch Required'}
                        </span>
                    </div>
                </div>
            </section>

            {/* Test Actions */}
            <section className="diagnostics-section">
                <h2>🧪 Test Actions</h2>
                <div className="actions-grid">
                    <button
                        onClick={connectWallet}
                        className="action-btn primary"
                        disabled={walletStatus.isConnected}
                    >
                        🔗 Test Connection
                    </button>
                    <button
                        onClick={forceRefresh}
                        className="action-btn secondary"
                    >
                        🔄 Force Refresh State
                    </button>
                    <button
                        onClick={disconnectWallet}
                        className="action-btn danger"
                        disabled={!walletStatus.isConnected}
                    >
                        🔌 Disconnect Wallet
                    </button>
                    <button
                        onClick={handleTestBalance}
                        className="action-btn primary"
                        disabled={!walletStatus.isConnected}
                    >
                        💰 Test Balance (Manual)
                    </button>
                    <button
                        onClick={() => {
                            localStorage.clear();
                            sessionStorage.clear();
                            alert("Storage cleared. Reloading...");
                            window.location.reload();
                        }}
                        className="action-btn danger"
                    >
                        🗑️ Reset All (Fix Logs)
                    </button>
                    <button
                        onClick={exportLogs}
                        className="action-btn secondary"
                    >
                        💾 Export Diagnostics
                    </button>
                </div>
            </section>

            {/* Real-Time Logs */}
            <section className="diagnostics-section">
                <div className="logs-header">
                    <h2>📋 Real-Time Debug Logs ({logs.length})</h2>
                    <div className="logs-controls">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={autoScroll}
                                onChange={(e) => setAutoScroll(e.target.checked)}
                            />
                            Auto-scroll
                        </label>
                    </div>
                </div>
                <div id="logs-container" className="logs-container">
                    {logs.length === 0 ? (
                        <div className="empty-logs">No logs yet. Try connecting a wallet.</div>
                    ) : (
                        logs.map((log, index) => (
                            <div key={index} className="log-entry">
                                <span className="log-timestamp">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                <span className="log-message">{log.message}</span>
                            </div>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
};

export default DiagnosticsView;
